/**
 * Iter 229 — Carte Quotas Meta (WhatsApp / Messenger / Instagram).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Activity, Save, Loader2, AlertTriangle, CheckCircle2, ShieldAlert } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", color: "#25d366", icon: "💬" },
  messenger: { label: "Messenger", color: "#0084ff", icon: "💙" },
  instagram: { label: "Instagram", color: "#e1306c", icon: "📸" },
};

const STATUS_META = {
  ok: { color: "#22c55e", icon: CheckCircle2, label: "OK" },
  info: { color: "#3b82f6", icon: Activity, label: "60–80%" },
  warning: { color: "#f59e0b", icon: AlertTriangle, label: "Attention 80–95%" },
  critical: { color: "#f43f5e", icon: ShieldAlert, label: "Critique ≥95%" },
};

export default function MetaQuotasCard() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [usage, setUsage] = useState(null);
  const [editing, setEditing] = useState(false);
  const [limits, setLimits] = useState({ whatsapp: 1000, messenger: 5000, instagram: 200 });
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/meta/quotas/usage");
      setUsage(r.data);
      const cur = {};
      (r.data?.channels || []).forEach((c) => { cur[c.channel] = c.limit; });
      setLimits({ ...limits, ...cur });
    } catch {
      setUsage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/meta/quotas/limits", limits);
      setEditing(false);
      await reload();
    } catch (e) {
      alert("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 space-y-4" data-testid="meta-quotas-card">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
            <Activity className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Quotas Meta (j+1)</h3>
            <p className="text-[11px] text-slate-500">Anticipez les rate-limits Meta avant blocage. Ajustez selon votre tier.</p>
          </div>
        </div>
        <Button onClick={() => setEditing(!editing)} size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="quota-edit-toggle">
          {editing ? "Annuler" : "Modifier seuils"}
        </Button>
      </div>

      {usage?.channels && (
        <div className="grid md:grid-cols-3 gap-3" data-testid="quota-channels">
          {usage.channels.map((c) => {
            const meta = CHANNEL_META[c.channel] || {};
            const stMeta = STATUS_META[c.status] || STATUS_META.ok;
            const StIcon = stMeta.icon;
            return (
              <div key={c.channel} className="bg-[#0b0811] border border-white/5 rounded-lg p-3" data-testid={`quota-${c.channel}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-bold uppercase" style={{ color: meta.color }}>{meta.icon} {meta.label}</span>
                  <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-bold" style={{ backgroundColor: `${stMeta.color}1f`, color: stMeta.color }}>
                    <StIcon className="w-2.5 h-2.5" /> {c.percent}%
                  </span>
                </div>
                <div className="text-xs font-bold text-white mb-1">
                  {c.used} <span className="text-slate-500 font-normal">/ {c.limit}</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-1.5">
                  <div className="h-full transition-all" style={{ width: `${Math.min(c.percent, 100)}%`, backgroundColor: stMeta.color }} />
                </div>
                <div className="text-[10px] text-slate-500">Restant aujourd'hui : <strong style={{ color: stMeta.color }}>{c.remaining}</strong></div>
                {editing && (
                  <Input
                    type="number"
                    value={limits[c.channel]}
                    onChange={(e) => setLimits({ ...limits, [c.channel]: parseInt(e.target.value) || 0 })}
                    className="bg-[#14101c] border-white/10 text-white text-xs h-7 mt-2"
                    data-testid={`quota-input-${c.channel}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-[#14101c]" data-testid="quota-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Enregistrer seuils
          </Button>
        </div>
      )}

      <p className="text-[10px] text-slate-500">
        Limites par défaut (Tier 1 Meta) : WhatsApp 1k/24h, Messenger 5k/24h, Instagram 200/24h.
        Adaptez selon votre tier (consultez Meta Business Manager).
      </p>
    </div>
  );
}
